import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Alert, CircularProgress, Dialog, DialogContent, DialogTitle } from "@mui/material"
import { MdCheckCircle, MdLogout, MdArrowForward } from "react-icons/md"

import ServicoAPI from "../../../servicos/ServicoAPI"
import { obterLocalizacaoProfissionalGestaoVaga } from "../../../servicos/gestaoVagaLocalizacao"
import { resolverIngressoVaga } from "./gestaoVagaIngressoMock.js"
import GestaoVagaResumoAposAcao from "./GestaoVagaResumoAposAcao.jsx"
import GestaoVagaAlertaJanelaIngresso from "./GestaoVagaAlertaJanelaIngresso.jsx"
import { encontrarIndiceLinhaDestaqueIngresso } from "./gestaoVagaDestaqueIngressoUtil.js"
import {
	aplicarRotuloVagaTexto,
	resolverRotulosGestaoVaga,
} from "./gestaoVagaRotuloUtil.js"

/**
 * Botões de ingresso ao lado de cada dia da grade (horários da vaga).
 */
export default function GestaoVagaHorariosIngresso({
	dados,
	idVaga,
	ingresso: ingressoProp,
	horariosLinhas = [],
	tituloVaga = "esta vaga",
	destaqueIngressoHoje = null,
	onIngressoAtualizado,
	rotulos,
}) {
	const txt = (texto) =>
		aplicarRotuloVagaTexto(texto, rotulos ?? resolverRotulosGestaoVaga())
	const [ingresso, setIngresso] = useState(() => resolverIngressoVaga({ ingresso: ingressoProp }))
	const [diaConfirmar, setDiaConfirmar] = useState(null)
	const [diaSair, setDiaSair] = useState(null)
	const [processando, setProcessando] = useState(false)
	const [feedbackLinha, setFeedbackLinha] = useState(null)
	const [resultadoAposAcao, setResultadoAposAcao] = useState(null)
	const [indiceDestaqueHoje, setIndiceDestaqueHoje] = useState(-1)
	const rowRefs = useRef([])
	const navegar = useNavigate()

	const idVagaNum = parseInt(idVaga, 10) || 0
	const janelaDias = ingresso?.janelaDias ?? 3
	const limitePorDia = ingresso?.limitePorDia ?? ingresso?.limiteTitulares ?? 1

	useEffect(() => {
		setIngresso(resolverIngressoVaga({ ingresso: ingressoProp }))
		setFeedbackLinha(null)
		setResultadoAposAcao(null)
	}, [ingressoProp])

	if (!dados || !idVagaNum) return null

	const linhasComSlot = horariosLinhas.filter((l) => l?.slotIngresso)

	useEffect(() => {
		if (!destaqueIngressoHoje || feedbackLinha?.tipo === "success") {
			setIndiceDestaqueHoje(-1)
			return undefined
		}
		const idx = encontrarIndiceLinhaDestaqueIngresso(horariosLinhas, destaqueIngressoHoje)
		if (idx < 0) {
			setIndiceDestaqueHoje(-1)
			return undefined
		}
		setIndiceDestaqueHoje(idx)
		const timer = window.setTimeout(() => {
			const el = rowRefs.current[idx]
			if (!el) return
			const scroll = el.closest(".gv-detalhe-sheet__scroll")
			if (scroll) {
				const elRect = el.getBoundingClientRect()
				const scrollRect = scroll.getBoundingClientRect()
				const top =
					scroll.scrollTop +
					(elRect.top - scrollRect.top) -
					scroll.clientHeight / 2 +
					elRect.height / 2
				scroll.scrollTo({ top: Math.max(0, top), behavior: "smooth" })
			} else {
				el.scrollIntoView({ behavior: "smooth", block: "center" })
			}
		}, 450)
		return () => window.clearTimeout(timer)
	}, [destaqueIngressoHoje, horariosLinhas, feedbackLinha])

	const aplicarIngressoRetorno = (payload) => {
		if (!payload?.ingresso) return
		const novo = resolverIngressoVaga({
			ingresso: payload.ingresso,
			qtdTitulares: payload.vaga?.qtdTitulares,
		})
		setIngresso(novo)
		setResultadoAposAcao(payload)
		onIngressoAtualizado?.(novo, payload)
	}

	const chaveLinha = (linha) => `${linha.diaSemana}-${linha.diaId}-${linha.texto}`

	const confirmarIngresso = async () => {
		const data = diaConfirmar?.data
		const linhaKey = diaConfirmar?.linhaKey
		if (!data) return
		setProcessando(true)
		if (linhaKey) setFeedbackLinha(null)
		try {
			const geo = await obterLocalizacaoProfissionalGestaoVaga()
			if (!geo) {
				const msg = txt("Ative a localização para ingressar na vaga.")
				if (linhaKey) {
					setFeedbackLinha({ linhaKey, tipo: "error", mensagem: msg })
					setDiaConfirmar(null)
				}
				return
			}
			const r = await ServicoAPI.gestaoVagasIngressarApp(dados, idVagaNum, data, geo)
			if (r?.resultado === true) {
				const label =
					diaConfirmar?.dataFormatada || r?.dados?.dataFormatada || data
				setDiaConfirmar(null)
				if (linhaKey) {
					setFeedbackLinha({
						linhaKey,
						tipo: "success",
						mensagem: r?.mensagem || `Ingresso confirmado para ${label}!`,
					})
				}
				aplicarIngressoRetorno(r?.dados)
			} else {
				const msg = r?.mensagem || "Não foi possível ingressar neste dia."
				setDiaConfirmar(null)
				if (linhaKey) {
					setFeedbackLinha({ linhaKey, tipo: "error", mensagem: msg })
				}
			}
		} catch {
			const msg = "Erro de conexão ao ingressar."
			setDiaConfirmar(null)
			if (linhaKey) {
				setFeedbackLinha({ linhaKey, tipo: "error", mensagem: msg })
			}
		} finally {
			setProcessando(false)
		}
	}

	const confirmarSair = async () => {
		const diaId = diaSair?.diaId
		const linhaKey = diaSair?.linhaKey
		if (!diaId) return
		setProcessando(true)
		if (linhaKey) setFeedbackLinha(null)
		try {
			const r = await ServicoAPI.gestaoVagasSairTitularApp(dados, idVagaNum, diaId)
			if (r?.resultado === true) {
				setDiaSair(null)
				if (linhaKey) {
					setFeedbackLinha({
						linhaKey,
						tipo: "success",
						mensagem: txt(r?.mensagem || "Você saiu da vaga neste dia."),
					})
				}
				aplicarIngressoRetorno(r?.dados)
			} else {
				const msg = txt(r?.mensagem || "Não foi possível sair da vaga.")
				setDiaSair(null)
				if (linhaKey) {
					setFeedbackLinha({ linhaKey, tipo: "error", mensagem: msg })
				}
			}
		} catch {
			const msg = txt("Erro de conexão ao sair da vaga.")
			setDiaSair(null)
			if (linhaKey) {
				setFeedbackLinha({ linhaKey, tipo: "error", mensagem: msg })
			}
		} finally {
			setProcessando(false)
		}
	}

	const renderAcao = (slot, ehDestaqueHoje, linhaKey, linha) => {
		if (!slot) return null
		if (slot.jaInscrito) {
			return (
				<div className="gv-horario-ingresso-row__acao-inscrito flex flex-col gap-2 w-full">
					<span className="gv-chip gv-chip--ok gv-chip--sm">Você já está</span>
					<button
						type="button"
						className="btnBorder btnFullW text-sm"
						onClick={() =>
							navegar(`/app/gestaoVaga/fila?idVaga=${idVagaNum}`)
						}
					>
						<span className="flex items-center justify-center gap-2">
							<MdArrowForward size={18} aria-hidden />
							Ir para a fila
						</span>
					</button>
					<button
						type="button"
						className="btnBorder btnFullW text-sm gv-btn-sair-vaga"
						disabled={processando}
						onClick={() => {
							setFeedbackLinha(null)
							setResultadoAposAcao(null)
							setDiaSair({
								diaId: slot.diaId,
								diaNome: linha?.diaNome || linha?.diaAbrev || "",
								dataFormatada: slot.dataFormatada,
								linhaKey,
							})
						}}
					>
						<span className="flex items-center justify-center gap-2">
							<MdLogout size={18} aria-hidden />
							{txt("Sair da vaga")}
						</span>
					</button>
				</div>
			)
		}
		if (
			slot.aguardandoJanela ||
			slot.status === "aguardando_janela" ||
			slot.aindaNaoAbriuHoje ||
			slot.status === "aguardando_abertura_hoje"
		) {
			return (
				<button
					type="button"
					className="gv-btn-ingresso-compact"
					disabled
					title={slot.mensagemJanelaHoje || ""}
				>
					Aguarde abertura
				</button>
			)
		}
		if (slot.janelaFilaEncerradaHoje || slot.status === "janela_fila_encerrada_hoje") {
			return (
				<button
					type="button"
					className="gv-btn-ingresso-compact"
					disabled
					title={slot.mensagemJanelaHoje || ""}
				>
					Encerrado hoje
				</button>
			)
		}
		if (slot.status === "conflito_horario" || slot.mensagemConflito) {
			return (
				<button type="button" className="gv-btn-ingresso-compact" disabled title={slot.mensagemConflito}>
					Horário em conflito
				</button>
			)
		}
		if (slot.podeIngressar) {
			return (
				<button
					type="button"
					className={`gv-btn-ingresso-compact${ehDestaqueHoje ? " gv-btn-ingresso-compact--destaque" : ""}`}
					disabled={processando}
					onClick={() => {
						setFeedbackLinha(null)
						setResultadoAposAcao(null)
						setDiaConfirmar({ ...slot, linhaKey })
					}}
				>
					Ingressar
				</button>
			)
		}
		return (
			<button type="button" className="gv-btn-ingresso-compact" disabled>
				Cheio ({slot.titularesNoDia}/{limitePorDia})
			</button>
		)
	}

	return (
		<div className="gv-horarios-ingresso">
			<Alert severity="info" className="mb-2 gv-alert-linha-toda gv-janela-info-alert">
				{txt("Você pode ingressar ")}
				<strong>hoje</strong>
				{txt(" nos dias da grade que ainda têm vaga. Também entre ")}
				<strong>amanhã</strong>
				{txt(" e ")}
				<strong>{janelaDias} dias à frente</strong>.
				{txt(" Dias fora dessa janela mostram quando a candidatura abrirá. Até ")}
				<strong>{limitePorDia}</strong>
				{txt(" titular(es) por dia da semana.")}
			</Alert>

			{resultadoAposAcao ? (
				<GestaoVagaResumoAposAcao dadosApi={resultadoAposAcao} tituloFallback={tituloVaga} />
			) : null}

			{processando ? (
				<div className="flex justify-center py-2">
					<CircularProgress size={24} />
				</div>
			) : null}

			{horariosLinhas.map((linha, index) => {
				const slot = linha.slotIngresso
				const rowKey = chaveLinha(linha)
				const ehDestaqueHoje = index === indiceDestaqueHoje
				const feedbackRow =
					feedbackLinha?.linhaKey === rowKey ? feedbackLinha : null
				const ocultarAcao = feedbackRow?.tipo === "success"
				const mostrarAlertas = !ocultarAcao && slot
				const alertaJanela = mostrarAlertas && slot.aguardandoJanela
				const alertaConflito = mostrarAlertas && slot.mensagemConflito
				const alertaEncerradoHoje =
					mostrarAlertas &&
					(slot.janelaFilaEncerradaHoje ||
						slot.status === "janela_fila_encerrada_hoje")
				const alertaAbreHoje =
					mostrarAlertas &&
					(slot.aindaNaoAbriuHoje ||
						slot.status === "aguardando_abertura_hoje")
				const mensagemEncerradoHoje = txt(
					(slot?.mensagemJanelaHoje && String(slot.mensagemJanelaHoje).trim()) ||
						(slot?.janelaHoje?.fechaEm
							? `O prazo para ingressar nesta vaga hoje encerrou em ${slot.janelaHoje.fechaEm}.`
							: slot?.janelaHoje?.horaInicio
							? `O prazo para ingressar nesta vaga hoje encerrou após ${slot.janelaHoje.horaInicio} + tolerância.`
							: "O prazo para ingressar nesta vaga hoje já encerrou.")
				)
				const mensagemAbreHoje =
					(slot?.mensagemJanelaHoje && String(slot.mensagemJanelaHoje).trim()) ||
					(slot?.janelaHoje?.abreEm
						? `O ingresso para hoje abre em ${slot.janelaHoje.abreEm}.`
						: "O ingresso para hoje ainda não abriu.")

				const tempoDestaque = destaqueIngressoHoje?.tempo
				const labelDestaqueData =
					slot?.dataFormatada ||
					tempoDestaque?.dataIngressoFormatada ||
					(destaqueIngressoHoje?.data
						? String(destaqueIngressoHoje.data).split("-").reverse().join("/")
						: "")

				return (
					<div
						key={`${linha.diaSemana}-${linha.diaId}-${linha.texto}`}
						ref={(node) => {
							rowRefs.current[index] = node
						}}
						className={`gv-horario-ingresso-row${ehDestaqueHoje ? " gv-horario-ingresso-row--destaque-hoje" : ""}`}
					>
						{ehDestaqueHoje ? (
							<Alert
								severity="info"
								className="gv-alert-linha-toda gv-horario-ingresso-row__destaque-alerta"
							>
								<strong>Ingresso de hoje</strong>
								{labelDestaqueData ? ` (${labelDestaqueData})` : ""}
								{destaqueIngressoHoje?.horario
									? ` · início ${destaqueIngressoHoje.horario}`
									: slot?.horario
									? ` · ${slot.horario}`
									: ""}
								{tempoDestaque?.ingressoAteAs
									? ` · prazo até ${tempoDestaque.ingressoAteAs}`
									: ""}
								— toque em <strong>Ingressar</strong> nesta linha.
								{tempoDestaque?.agoraFormatado ? (
									<>
										<br />
										<small>
											Servidor: agora {tempoDestaque.agoraFormatado} ({tempoDestaque.fusoHorario})
											{tempoDestaque.agoraDentroDaJanela === false
												? " — fora da janela"
												: ""}
										</small>
									</>
								) : null}
							</Alert>
						) : null}
						<div className="gv-horario-ingresso-row__main">
							<div className="gv-horario-ingresso-row__info">
								<div className="gv-horario-ingresso-row__titulo">
									<span className="gv-horario-ingresso-row__dia">
										{linha.diaNome || linha.diaAbrev}
									</span>
									{slot?.dataFormatada ? (
										<span className="gv-horario-ingresso-row__data">
											{slot.dataFormatada}
										</span>
									) : null}
								</div>
								<p className="gv-horario-ingresso-row__texto m-0">{linha.texto}</p>
								{slot && !ocultarAcao && slot.podeIngressar ? (
									<p className="gv-horario-ingresso-row__ocupacao m-0">
										{slot.titularesNoDia}/{limitePorDia} titulares
										{slot.vagasRestantes > 0
											? txt(` · ${slot.vagasRestantes} vaga(s)`)
											: ""}
									</p>
								) : null}
							</div>
							{slot && !ocultarAcao ? (
								<div className="gv-horario-ingresso-row__acao">
									{feedbackRow?.mensagem ? (
										<Alert
											severity={feedbackRow.tipo === "success" ? "success" : "error"}
											className="gv-alert-linha-toda gv-horario-ingresso-row__feedback mb-2"
											icon={
												feedbackRow.tipo === "success" ? (
													<MdCheckCircle size={20} />
												) : undefined
											}
										>
											{txt(feedbackRow.mensagem)}
										</Alert>
									) : null}
									{renderAcao(slot, ehDestaqueHoje, rowKey, linha)}
								</div>
							) : feedbackRow?.mensagem ? (
								<div className="gv-horario-ingresso-row__acao">
									<Alert
										severity={feedbackRow.tipo === "success" ? "success" : "error"}
										className="gv-alert-linha-toda gv-horario-ingresso-row__feedback"
										icon={
											feedbackRow.tipo === "success" ? (
												<MdCheckCircle size={20} />
											) : undefined
										}
									>
										{txt(feedbackRow.mensagem)}
									</Alert>
								</div>
							) : null}
						</div>
						{alertaJanela || alertaConflito || alertaEncerradoHoje || alertaAbreHoje ? (
							<div className="gv-horario-ingresso-row__alertas">
								{alertaJanela ? (
									<GestaoVagaAlertaJanelaIngresso
										dataInicioIngresso={slot.dataInicioIngresso}
										dataInicioIngressoFormatada={slot.dataInicioIngressoFormatada}
									/>
								) : null}
								{alertaEncerradoHoje ? (
									<Alert
										severity="warning"
										className="gv-alert-linha-toda gv-alerta-janela-ingresso"
									>
										{mensagemEncerradoHoje}
									</Alert>
								) : null}
								{alertaAbreHoje ? (
									<Alert
										severity="warning"
										className="gv-alert-linha-toda gv-alerta-janela-ingresso"
									>
										{mensagemAbreHoje}
									</Alert>
								) : null}
								{alertaConflito ? (
									<Alert
										severity="warning"
										className="gv-alert-linha-toda gv-alerta-janela-ingresso"
									>
										{slot.mensagemConflito}
									</Alert>
								) : null}
							</div>
						) : null}
					</div>
				)
			})}

			<Dialog
				open={Boolean(diaConfirmar)}
				onClose={() => !processando && setDiaConfirmar(null)}
				fullWidth
				maxWidth="xs"
				className="gv-dialog-confirmar-ingresso"
				sx={{ zIndex: (theme) => theme.zIndex.modal + 3 }}
			>
				<DialogTitle>Confirmar ingresso</DialogTitle>
				<DialogContent>
					<p className="m-0 mb-2">
						Tornar-se titular em <strong>{tituloVaga}</strong> em{" "}
						<strong>{diaConfirmar?.diaNome}</strong>
						{diaConfirmar?.dataFormatada
							? ` (${diaConfirmar.dataFormatada})`
							: ""}
						?
					</p>
					<button
						type="button"
						className="btnFullW my-2"
						disabled={processando}
						onClick={confirmarIngresso}
					>
						{processando ? "Confirmando…" : "Sim, ingressar"}
					</button>
					<button
						type="button"
						className="btnBorder btnFullW my-1"
						disabled={processando}
						onClick={() => setDiaConfirmar(null)}
					>
						Cancelar
					</button>
				</DialogContent>
			</Dialog>

			<Dialog
				open={Boolean(diaSair)}
				onClose={() => !processando && setDiaSair(null)}
				fullWidth
				maxWidth="xs"
				className="gv-dialog-confirmar-ingresso"
				sx={{ zIndex: (theme) => theme.zIndex.modal + 3 }}
			>
				<DialogTitle>{txt("Sair da vaga")}</DialogTitle>
				<DialogContent>
					<p className="m-0 mb-2">
						Deseja sair de <strong>{tituloVaga}</strong>
						{diaSair?.diaNome ? (
							<>
								{" "}em <strong>{diaSair.diaNome}</strong>
							</>
						) : null}
						? Você deixará de ser titular nesse dia e não receberá mais os
						serviços dele.
					</p>
					<button
						type="button"
						className="btnFullW my-2"
						disabled={processando}
						onClick={confirmarSair}
					>
						{processando ? "Saindo…" : txt("Sim, sair da vaga")}
					</button>
					<button
						type="button"
						className="btnBorder btnFullW my-1"
						disabled={processando}
						onClick={() => setDiaSair(null)}
					>
						Cancelar
					</button>
				</DialogContent>
			</Dialog>
		</div>
	)
}
